/**
 * Achievement Card Backside Component
 * Shows the Intraction logo on the back of achievement cards
 */

import React from 'react'
import { Box } from '@mui/material'
import { INTERAX_COLORS } from '../../../../constants'

export interface AchievementCardBacksideProps {
  size?: 'small' | 'medium' | 'large'
}

// Card size matches the actual SessionAchievementBadge size (same as front card)
// These match minWidth/maxWidth from SessionAchievementBadge
const CARD_SIZE_MAP = {
  small: 130, // Matches SessionAchievementBadge minWidth/maxWidth for 'small'
  medium: 180, // Matches SessionAchievementBadge minWidth/maxWidth for 'medium'
  large: 200 // Matches SessionAchievementBadge minWidth/maxWidth for 'large'
}

// Logo size matches the icon size on the front card
const LOGO_SIZE_MAP = {
  small: 80, // Matches iconSize for 'small' in SessionAchievementBadge
  medium: 110, // Matches iconSize for 'medium' in SessionAchievementBadge
  large: 142 // Matches iconSize for 'large' in SessionAchievementBadge
}

/**
 * AchievementCardBackside - Displays the Intraction logo on card backside
 * Card structure matches SessionAchievementBadge exactly: same padding, styling, and layout
 */
export const AchievementCardBackside: React.FC<AchievementCardBacksideProps> = ({ size = 'small' }) => {
  const cardSize = CARD_SIZE_MAP[size]
  const logoSize = LOGO_SIZE_MAP[size]

  return (
    <Box
      component="article"
      sx={{
        display: 'flex',
        height: '180px',
        flexDirection: 'column',
        alignItems: 'center',
        padding: size === 'small' ? 2 : 3, // Same padding as SessionAchievementBadge
        gap: 2, // Same gap as SessionAchievementBadge
        minWidth: cardSize,
        maxWidth: cardSize,
        transition: 'all 0.3s ease-in-out',
        background: 'linear-gradient(144.63deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)', // Same as SessionAchievementBadge
        border: '1px solid rgba(255, 255, 255, 0.08)', // Same as SessionAchievementBadge
        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.2), inset 0px 1px 0px 1px rgba(255, 255, 255, 0.1)', // Same as SessionAchievementBadge
        backdropFilter: 'blur(6px)', // Same as SessionAchievementBadge
        borderRadius: '16px'
      }}
    >
      {/* Logo - matches icon structure from SessionAchievementBadge */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: logoSize,
          height: logoSize
        }}
      >
        <Box
          component="img"
          src="/assets/img/logo_intr_load.png"
          alt="Intraction Logo"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }}
        />
      </Box>
      {/* Achievement Title */}
      <Box
        component="h3"
        sx={{
          fontFamily: 'Lexend Deca, sans-serif',
          fontWeight: 700,
          fontSize: size === 'small' ? '14px' : size === 'large' ? '18px' : '16px',
          lineHeight: size === 'small' ? '18px' : size === 'large' ? '22px' : '20px',
          margin: 0,
          padding: '0 4px',
          minHeight: size === 'small' ? '36px' : '40px',
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
          maxWidth: '100%',
          whiteSpace: 'normal',
          background: `linear-gradient(142.35deg, ${INTERAX_COLORS.sun} 22.68%, #FFD700 42.5%, #FFEEAA 55.31%, #FFD14E 69.6%, ${INTERAX_COLORS.sun} 83.62%)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          textFillColor: 'transparent'
        }}
      >
        {'Intraction'}
      </Box>

      {/* Empty space below logo (matches gap structure from SessionAchievementBadge) */}
    </Box>
  )
}
